import { Controller as BaseController } from "@hotwired/stimulus"
import Uppy from "@uppy/core"
import XHRUpload from "@uppy/xhr-upload"
import AwsS3 from "@uppy/aws-s3"
import Dashboard from "@uppy/dashboard"

import "@uppy/core/css/style.min.css"
import "@uppy/dashboard/css/style.min.css"

export class Controller extends BaseController {
  static targets = ["input", "submit"]
  static values = {
    service: String,
    types: Array,
    action: String,
    dimensions: Array,
    debug: Boolean,
  }

  connect() {
    this.submitTarget.classList.add("hidden")
    this.inputTarget.classList.add("hidden")

    this.uppy = new Uppy({
      debug: this.debugValue,
      id: this.inputTarget.id,
      allowMultipleUploadBatches: true,
      restrictions: {
        allowedFileTypes: this.typesValue,
      },
    })

    if (this.serviceValue == "s3") {
      this.uppy.use(AwsS3, {
        // Empty string keeps requests same-origin: Uppy builds `${endpoint}/s3/...`,
        // so a "/" here would produce a protocol-relative "//s3/..." URL.
        companionEndpoint: "", // Shrine"s presign endpoint mounted on `/s3/params`
        shouldUseMultipart: false,
      })
    } else if (this.serviceValue == "s3_multipart") {
      this.uppy.use(AwsS3, {
        // Empty string keeps requests same-origin (see above).
        companionEndpoint: "", // this.uppy-s3_multipart endpoint mounted on `/s3/multipart`
        shouldUseMultipart: (file) => {
          return file.size > 100 * 2 ** 20 // 100MB
        },
      })
    } else {
      this.uppy.use(XHRUpload, {
        endpoint: "/upload", // Shrine"s upload endpoint
      })
    }

    this.uppy.use(Dashboard, {
      target: this.inputTarget.parentNode,
      inline: true,
      height: "auto",
      width: "100%",
      theme: "dark",
    })

    this.uppy.on("upload-success", (file, response) => {
      this._sendToServer(file, response)
    })
  }

  async _sendToServer(file, response) {
    const action = this.actionValue

    // Fetch to POST this to the server
    try {
      const serverResponse = await fetch(action, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name=csrf-token]")
            .content,
        },
        body: this._uploadedFileData(file, response, this.serviceValue),
      })

      const responseStatus = serverResponse.status
      const responseBody = await serverResponse.json()

      if (responseStatus > 400) {
        this.uppy.info(responseBody.errors, "error")
        this.uppy.removeFile(file.id)
      }
    } catch (error) {
      this.uppy.info(error.message, "error")
      this.uppy.removeFile(file.id)
    }
  }

  disconnect() {
    if (this.uppy) {
      this.uppy.destroy()
    }
  }

  _uploadedFileData(file, response, server) {
    if (server == "s3") {
      const id = response.body.key.match(/^cache\/(.+)/)[1] // object key without prefix

      return JSON.stringify(this._fileData(file, id))
    } else if (server == "s3_multipart") {
      // Shrine's multipart endpoint only returns the object URL when the upload
      // completes, so the cache key is extracted from it (without the prefix).
      const cacheKey = response.body.key ?? response.uploadURL
      const id = cacheKey.match(/cache\/([^\?]+)/)[1]

      return JSON.stringify(this._fileData(file, id))
    } else {
      return JSON.stringify(response.body)
    }
  }

  _fileData(file, id) {
    return {
      id: id,
      storage: "cache",
      metadata: {
        size: file.size,
        filename: file.name,
        mime_type: file.type,
      },
    }
  }
}
